// @ts-check
// Pedidos feitos pelo site matriculamais.com no painel de suporte: lista com situação, módulos, valor e forma de
// pagamento, e a página do pedido com os dados cadastrais, o pagamento no Asaas, o link de acesso e o reenvio do e-mail.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link, searchParamsState, updateSearchParams } from '../../../app/router.js';
import { fetchAdminSiteOrder, fetchAdminSiteOrders, resendAdminSiteOrderEmail } from '../../../data/admin-finance.js';
import { formatDate, formatDateTime } from '../../../lib/format.js';
import { adminInstitutionPath, adminPath } from '../../../lib/admin-navigation.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { showOperationsError } from '../operations-format.js';
import { AdminEmpty, AdminLoading, AdminPage, AdminSection, DefinitionList, ExternalLink, FilterField, FilterSelect, PaymentStatusTag, SiteOrderStatusTag, billingTypeLabel, enumLabel, formatDay, formatMoney, formatTaxDocument } from './admin-ui.js';
import { t } from '../../../i18n/index.js';

const STATUS_FILTERS = ['ALL', 'PENDING', 'CONFIRMED', 'OVERDUE', 'CANCELED'];

/** @param {string} key @param {Record<string, unknown>} [values] */
const text = (key, values) => t(`operations.admin.panel.siteOrders.${key}`, values);

export function AdminSiteOrdersPage() {
  const parameters = searchParamsState.value;
  const status = STATUS_FILTERS.includes(parameters.get('status') ?? '') ? /** @type {string} */ (parameters.get('status')) : 'ALL';
  const [orders, setOrders] = useState(/** @type {any[] | null} */ (null));
  useEffect(() => {
    setOrders(null);
    fetchAdminSiteOrders({ status: status === 'ALL' ? undefined : status }).then((result) => setOrders(result.orders)).catch(showOperationsError);
  }, [status]);

  return html`<${AdminPage} icon="IconCash" title=${t('operations.admin.panel.sections.site-orders')} subtitle=${text('subtitle')} wide=${true}>
    <div class="admin-toolbar">
      <${FilterField} label=${text('status')}>
        <${FilterSelect} value=${status} ariaLabel=${text('status')} onChange=${(/** @type {string} */ value) => updateSearchParams({ status: value === 'ALL' ? null : value })}
          options=${STATUS_FILTERS.map((value) => ({ value, label: value === 'ALL' ? text('allStatuses') : t(`operations.admin.panel.siteOrderStatuses.${value}`) }))} />
      </${FilterField}>
    </div>
    ${orders === null ? html`<${AdminLoading} rows=${4} />` : orders.length === 0 ? html`<${AdminEmpty} icon="IconCash" title=${text('empty')} />`
      : html`<div class="settings-table admin-table">
          <div class="settings-table-row settings-table-header admin-order-row">
            <span>${text('institution')}</span><span>${text('plan')}</span><span>${text('createdAt')}</span><span>${text('billingType')}</span>
            <span class="settings-table-number">${text('today')}</span><span>${text('status')}</span><span></span>
          </div>
          ${orders.map((order) => html`<${Link} key=${order.id} href=${adminPath('site-orders', order.id)} className="settings-table-row admin-order-row admin-row-link">
            <span class="admin-cell-stack"><span>${order.institutionName}</span><span class="settings-table-muted">${order.slug} · ${order.contactEmail}</span></span>
            <span class="admin-cell-stack"><span>${order.modulesLabel}</span><span class="settings-table-muted">${enumLabel('periods', order.period)}${order.setup ? ` · ${text('withSetup')}` : ''}${order.importLeads ? ` · ${text('withImport', { count: order.importLeads })}` : ''}</span></span>
            <span class="settings-table-muted">${formatDate(order.createdAt)}</span>
            <span class="settings-table-muted">${billingTypeLabel(order.billingType)}</span>
            <span class="settings-table-number admin-cell-money">${formatMoney(order.amounts?.today)}</span>
            <span><${SiteOrderStatusTag} status=${order.status} /></span>
            <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${14} /></span>
          </${Link}>`)}
        </div>`}
  </${AdminPage}>`;
}

/** @param {{ orderId: string }} props */
export function AdminSiteOrderPage({ orderId }) {
  const [detail, setDetail] = useState(/** @type {any} */ (null));
  const [isMissing, setIsMissing] = useState(false);
  const [isResending, setIsResending] = useState(false);
  useEffect(() => {
    setDetail(null);
    fetchAdminSiteOrder(orderId).then(setDetail).catch((error) => {
      if (/** @type {any} */ (error)?.statusCode === 404) setIsMissing(true);
      else showOperationsError(error);
    });
  }, [orderId]);

  const resend = async () => {
    setIsResending(true);
    try {
      const { contactEmail } = await resendAdminSiteOrderEmail(orderId);
      showSuccessSnackbar(text('resent', { email: contactEmail }));
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsResending(false);
    }
  };

  const back = { href: adminPath('site-orders'), label: t('common.back') };
  if (isMissing) {
    return html`<${AdminPage} icon="IconCash" title=${t('operations.admin.panel.sections.site-orders')} back=${back}><${AdminEmpty} icon="IconCash" title=${text('notFound')} /></${AdminPage}>`;
  }
  const order = detail?.order;
  const amounts = order?.amounts ?? {};
  const canResend = order?.status === 'CONFIRMED' && !order?.tenantId;
  return html`<${AdminPage} icon="IconCash" title=${order ? order.institutionName : '…'} back=${back} wide=${true}
    actions=${order ? html`<div class="admin-headline"><${SiteOrderStatusTag} status=${order.status} />
      ${canResend ? html`<${Button} title=${text('resend')} icon="IconSend" variant="secondary" size="small" isLoading=${isResending} onClick=${resend} />` : null}</div>` : null}>
    ${!detail ? html`<${AdminLoading} rows=${3} />` : html`
      <p class="admin-subtitle">${text('orderId', { id: order.id })} · ${formatDateTime(order.createdAt)}</p>
      ${order.status === 'CONFIRMED' ? html`<div class="admin-notice" data-tone=${order.tenantId ? 'positive' : 'info'} role="status">
        <${Icon} name=${order.tenantId ? 'IconCircleCheck' : 'IconKey'} size=${16} />
        ${order.tenantId
          ? html`<span>${text('attached')} <${Link} href=${adminInstitutionPath(order.tenantId)}>${detail.tenant?.displayName ?? order.slug}</${Link}></span>`
          : html`<span>${text('awaitingSignUp')}${order.accessUrl ? html` <a href=${order.accessUrl} target="_blank" rel="noopener noreferrer">${text('openAccessLink')}</a>` : null}</span>`}
      </div>` : null}
      <div class="admin-two-columns">
        <${AdminSection} title=${text('customerTitle')}>
          <${DefinitionList} columns=${1} items=${[
            { label: text('institution'), value: order.institutionName },
            { label: text('institutionType'), value: order.institutionType || '-' },
            { label: text('cnpj'), value: formatTaxDocument(order.cnpj) },
            { label: text('slug'), value: order.slug, mono: true },
            { label: text('contact'), value: order.contactName },
            { label: text('email'), value: order.contactEmail },
            { label: text('whatsapp'), value: order.contactWhatsapp || '-' },
            { label: text('city'), value: order.city || '-' },
          ]} />
        </${AdminSection}>
        <${AdminSection} title=${text('orderTitle')}>
          <${DefinitionList} columns=${1} items=${[
            { label: text('plan'), value: `${order.modulesLabel} (${enumLabel('periods', order.period)})` },
            { label: text('planKey'), value: order.planKey, mono: true },
            { label: text('setup'), value: order.setup ? formatMoney(amounts.setup) : t('operations.admin.panel.common.no') },
            { label: text('import'), value: order.importLeads ? `${text('withImport', { count: order.importLeads })} (${formatMoney(amounts.importValue)})${order.importSource ? ` · ${order.importSource}` : ''}` : t('operations.admin.panel.common.no') },
            order.couponCode ? { label: text('coupon'), value: `${order.couponCode} (${formatMoney(amounts.discount)})` } : null,
            { label: text('recurring'), value: `${formatMoney(amounts.renewal ?? amounts.recurring)} ${enumLabel('periodEvery', order.period)}` },
            { label: text('today'), value: html`<strong>${formatMoney(amounts.today)}</strong>` },
            { label: text('billingType'), value: billingTypeLabel(order.billingType) },
            { label: text('dueDate'), value: formatDay(order.dueDate) },
            order.confirmedAt ? { label: text('confirmedAt'), value: formatDateTime(order.confirmedAt) } : null,
          ]} />
        </${AdminSection}>
      </div>
      <${AdminSection} title=${text('paymentTitle')} description=${text('paymentDescription')}>
        ${detail.payment ? html`<div class="admin-headline"><span class="admin-dialog-amount">${formatMoney(detail.payment.value)}</span><${PaymentStatusTag} status=${detail.payment.status} /></div>
          <${DefinitionList} columns=${3} items=${[
            { label: text('paymentId'), value: detail.payment.id, mono: true },
            { label: text('customerId'), value: order.asaasCustomerId ?? '-', mono: true },
            { label: text('subscriptionId'), value: order.asaasSubscriptionId ?? '-', mono: true },
            { label: text('dueDate'), value: formatDay(detail.payment.dueDate) },
            { label: text('paymentDate'), value: formatDay(detail.payment.clientPaymentDate ?? detail.payment.paymentDate) },
            { label: text('netValue'), value: formatMoney(detail.payment.netValue) },
          ]} />
          <div class="admin-link-row"><${ExternalLink} href=${detail.payment.invoiceUrl ?? order.invoiceUrl} label=${t('operations.admin.panel.payments.openInvoiceUrl')} /><${ExternalLink} href=${detail.payment.bankSlipUrl} label=${t('operations.admin.panel.payments.openBankSlip')} icon="IconFileText" /></div>`
          : html`<${DefinitionList} columns=${3} items=${[
            { label: text('paymentId'), value: order.asaasPaymentId ?? '-', mono: true },
            { label: text('customerId'), value: order.asaasCustomerId ?? '-', mono: true },
            { label: text('subscriptionId'), value: order.asaasSubscriptionId ?? '-', mono: true },
          ]} /><div class="admin-link-row"><${ExternalLink} href=${order.invoiceUrl} label=${t('operations.admin.panel.payments.openInvoiceUrl')} /></div>`}
      </${AdminSection}>
    `}
  </${AdminPage}>`;
}
